import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, Sparkles, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { images, restaurantInfo } from '../data/mock';

const YhteystiedotPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: 'general', message: '' });
    }, 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-stone-800 via-stone-900 to-stone-800 overflow-hidden">
        <div className="absolute top-10 left-10 w-32 h-32 border-4 border-emerald-500/20 rounded-full" />
        <div className="absolute bottom-10 right-20 w-48 h-48 border-4 border-emerald-500/10 rounded-full" />
        
        <div className="absolute inset-0 opacity-30">
          <img
            src={images.contactHero}
            alt="Ravintola Data"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="font-handwritten text-4xl text-emerald-400 block mb-2">Ota yhteyttä!</span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Yhteystiedot
          </h1>
          <p className="text-lg text-stone-300 max-w-2xl mx-auto">
            Löydä perille, soita tai lähetä viesti – 
            <span className="font-handwritten text-xl text-emerald-400"> vastaamme aina!</span>
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <span className="font-handwritten text-3xl text-emerald-500">Tervetuloa!</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-8 mt-2">
                Löydä meille
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-emerald-50 p-5 rounded-2xl border-2 border-emerald-100 transform -rotate-[0.3deg] hover:rotate-0 transition-transform">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-1">Osoite</h3>
                    <p className="text-stone-600">{restaurantInfo.address}</p>
                    <a 
                      href={`https://maps.google.com/?q=${restaurantInfo.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-700 hover:text-emerald-800 text-sm mt-2 inline-flex items-center gap-1 font-medium"
                    >
                      Avaa kartalla →
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-amber-50 p-5 rounded-2xl border-2 border-amber-100 transform rotate-[0.3deg] hover:rotate-0 transition-transform">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-1">Puhelin</h3>
                    <a 
                      href={`tel:${restaurantInfo.phone}`}
                      className="text-stone-600 hover:text-emerald-700 transition-colors text-lg"
                    >
                      {restaurantInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-rose-50 p-5 rounded-2xl border-2 border-rose-100 transform -rotate-[0.2deg] hover:rotate-0 transition-transform">
                  <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-1">Sähköposti</h3>
                    <a 
                      href={`mailto:${restaurantInfo.email}`}
                      className="text-stone-600 hover:text-emerald-700 transition-colors"
                    >
                      {restaurantInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-stone-50 p-5 rounded-2xl border-2 border-stone-200 transform rotate-[0.2deg] hover:rotate-0 transition-transform">
                  <div className="w-14 h-14 bg-gradient-to-br from-stone-600 to-stone-700 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Clock className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-2">Aukioloajat</h3>
                    <div className="text-stone-600 space-y-1">
                      <p><span className="font-handwritten text-lg text-emerald-700">Lounas:</span> ma-pe {restaurantInfo.lunchHours.weekdays}</p>
                      <p><span className="font-handwritten text-lg text-green-600">Kahvila:</span> ma-pe {restaurantInfo.cafeHours.weekdays}</p>
                      <p className="text-stone-500 text-sm mt-2 bg-stone-100 px-3 py-1 rounded-full inline-block">Resq ruuat klo 15 asti</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <span className="font-handwritten text-3xl text-emerald-500">Kerro meille!</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-8 mt-2">
                Lähetä viesti
              </h2>
              
              {isSubmitted ? (
                <div className="bg-green-50 border-2 border-green-200 rounded-3xl p-8 text-center transform rotate-[0.5deg]">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Kiitos viestistäsi!</h3>
                  <p className="text-green-700">Palaamme asiaan mahdollisimman pian. <Heart className="w-4 h-4 inline text-red-400" /></p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5 bg-stone-50 p-6 md:p-8 rounded-3xl border-2 border-stone-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-stone-700 font-medium">Nimi *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-2 bg-white border-2 border-stone-200 focus:border-emerald-400 rounded-xl py-3"
                        placeholder="Nimesi"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-stone-700 font-medium">Puhelin</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2 bg-white border-2 border-stone-200 focus:border-emerald-400 rounded-xl py-3"
                        placeholder="Puhelinnumero"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-stone-700 font-medium">Sähköposti *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 bg-white border-2 border-stone-200 focus:border-emerald-400 rounded-xl py-3"
                      placeholder="sahkoposti@esimerkki.fi"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-stone-700 font-medium">Aihe</Label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="mt-2 w-full px-4 py-3 bg-white border-2 border-stone-200 rounded-xl focus:border-emerald-400 focus:outline-none"
                    >
                      <option value="general">Yleinen kysymys</option>
                      <option value="catering">Pitopalvelukysely</option>
                      <option value="feedback">Palaute</option>
                      <option value="other">Muu asia</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-stone-700 font-medium">Viesti *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="mt-2 bg-white border-2 border-stone-200 focus:border-emerald-400 rounded-xl"
                      placeholder="Kirjoita viestisi tähän..."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-700 hover:to-green-600 text-white py-4 text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all group"
                  >
                    Lähetä viesti
                    <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>

                  <p className="text-sm text-stone-500 text-center flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    Pitopalvelukyselyihin vastaamme 1–3 arkipäivän kuluessa.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Closing Message - Warm */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-500 to-green-500 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <span className="font-handwritten text-4xl text-emerald-200 block mb-4">Nähdään pian!</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Tervetuloa meille!
          </h2>
          <p className="text-lg text-emerald-100 leading-relaxed">
            Olemme täällä sinua varten – arkisin lounaasta iltapäiväkahveihin. 
            <span className="font-handwritten text-xl text-white block mt-4">Astuthan sisään, otamme sinut vastaan hyvällä ruoalla ja lämpimällä hymyllä!</span>
          </p>
          <Heart className="w-8 h-8 text-white mx-auto mt-8 animate-pulse" />
        </div>
      </section>
    </div>
  );
};

export default YhteystiedotPage;
